"use client";

import { useState } from "react";
import { Copy, Check, Link2, Pencil, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { useUpdateCustomSlug } from "../hooks/useRewardsData";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface ReferralLinkCardProps {
  referralUrl: string;
  code: string;
  customSlug: string | null;
}

export default function ReferralLinkCard({
  referralUrl,
  code,
  customSlug,
}: ReferralLinkCardProps) {
  const [copied, setCopied] = useState(false);
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugValue, setSlugValue] = useState(customSlug || "");
  const updateSlug = useUpdateCustomSlug();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveSlug = () => {
    // DEMO SANDBOX: the link is shared furniture, not the visitor's to rename.
    //
    // Seats are recycled, and the referral slug is NOT in the reset's table list — a
    // visitor who renamed it would leave that name on the seat for everyone after them,
    // and a slug is globally unique so it could also collide with a real agent's. Offer
    // the trial instead, which is the honest answer: your own link comes with an account.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Customizing your referral link is disabled in the demo sandbox. Start a free trial to claim your own link.',
        }),
      );
      setEditingSlug(false);
      return;
    }

    const trimmed = slugValue.trim();
    if (!trimmed || trimmed === customSlug) {
      setEditingSlug(false);
      return;
    }
    updateSlug.mutate(trimmed, {
      onSuccess: () => setEditingSlug(false),
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Your Referral Link
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-50 border rounded-lg px-3 py-2 text-sm text-gray-700 truncate font-mono">
            {referralUrl}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="flex-shrink-0"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>

        {editingSlug ? (
          <div className="flex items-center gap-2">
            <Input
              value={slugValue}
              onChange={(e) =>
                setSlugValue(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ""))
              }
              placeholder="my-custom-slug"
              className="flex-1 h-9 text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveSlug();
                if (e.key === "Escape") setEditingSlug(false);
              }}
            />
            <Button
              size="sm"
              onClick={handleSaveSlug}
              disabled={updateSlug.isPending}
            >
              {updateSlug.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditingSlug(false);
                setSlugValue(customSlug || "");
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setEditingSlug(true)}
            className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
          >
            <Pencil className="h-3 w-3" />
            Customize your link
          </button>
        )}

        {updateSlug.isError && (
          <p className="text-xs text-red-600">
            Failed to update slug. It may already be taken.
          </p>
        )}

        <p className="text-xs text-gray-500">
          Your code: <span className="font-mono font-medium">{customSlug || code}</span>
        </p>
      </CardContent>
    </Card>
  );
}
